import React, { useEffect, useState } from "react";
import axios from "axios";
import { Alert, CircularProgress, IconButton, Stack, TextField } from "@mui/material";
import { Search } from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import PatriotResultList from "./PatriotResultList";
import { PatriotSearchApiResponse } from "./types";

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

function getResultCount(response: PatriotSearchApiResponse | null): number {
  if (!response) return 0;
  const value = response.value ?? response.Value;
  const results = value?.results ?? value?.Results ?? [];
  return results.length;
}

function getErrorMessage(error: unknown): string {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;
    if (typeof responseData === "string") {
      return responseData;
    }

    if (responseData) {
      return JSON.stringify(responseData);
    }

    return error.message;
  }

  return "Something went wrong while searching Patriot.";
}

export default function PatriotNameSearch() {
  const query = useQuery();
  const urlName = query.get("name");
  const [name, setName] = useState("");
  const [score, setScore] = useState("89");
  const [nameError, setNameError] = useState(false);
  const [response, setResponse] = useState<PatriotSearchApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [noResults, setNoResults] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (urlName) {
      setName(urlName);
      onSubmit(urlName);
    }
  }, [urlName]);

  const onSubmit = async (passedInName: string) => {
    if (!passedInName) {
      setNameError(true);
      return;
    }

    setLoading(true);
    setErrorMessage("");
    setNoResults(false);

    try {
      const { data } = await axios.get<PatriotSearchApiResponse>("/api/blacklist/searchPatriot", {
        params: {
          name: passedInName,
          score
        }
      });
      setResponse(data);
      setNoResults(getResultCount(data) === 0);
    } catch (error) {
      setResponse(null);
      setErrorMessage(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(name);
        }}
      >
        <Stack direction="row">
          <div style={{ display: "flex", gap: 8 }}>
            <TextField
              error={nameError}
              label="Search Name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setNameError(false);
                setNoResults(false);
                setErrorMessage("");
              }}
              helperText={nameError ? <div style={{ color: "red" }}>Please Enter a Name</div> : ""}
            />
            <TextField
              type="number"
              label="Threshold"
              value={score}
              onChange={(e) => {
                setScore(e.target.value);
              }}
            />
            <IconButton type="submit" color="primary">
              {loading ? <CircularProgress size={24} /> : <Search />}
            </IconButton>
          </div>
        </Stack>
      </form>
      <div style={{ marginTop: 16 }}>
        {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
        {noResults && <div>No results found for {name}</div>}
        {response && <PatriotResultList response={response} />}
      </div>
    </>
  );
}
